import React, { useState, useEffect } from "react";
import Icon from "./Icon";
import * as api from "../../../services/api";

function ManageAccessModal({ file, onClose, onUpdated, showToast }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [visibility, setVisibility] = useState("private");
  const [sharedWith, setSharedWith] = useState([]); // [{email, role, addedDate}]
  const [newEmail, setNewEmail] = useState("");
  const [newRole, setNewRole] = useState("viewer");

  const isFolder = file && file.type === "folder";

  // Get the ID for this item (file or folder)
  const getItemId = () => {
    if (isFolder) {
      const folderPath = file.path === "/" ? `/${file.name}` : `${file.path}/${file.name}`;
      return `FOLDER#${folderPath}`;
    }
    return file.id || file.item_id;
  };

  useEffect(() => {
    if (file) loadPermissions();
  }, [file]);

  const loadPermissions = async () => {
    setLoading(true);
    try {
      const itemId = getItemId();
      const res = await api.getPermissions(itemId);
      const perms = res.permissions || res;
      setVisibility(perms.visibility || "private");

      // Build shared_with with details
      if (perms.shared_details && perms.shared_details.length > 0) {
        setSharedWith(perms.shared_details.map(u => ({
          email: u.email || u.principal_id || "",
          role: u.role || "viewer",
          addedDate: u.addedDate || u.added_date || ""
        })));
      } else if (perms.shared_with && perms.shared_with.length > 0) {
        setSharedWith(perms.shared_with.map(email => ({
          email: typeof email === "string" ? email : email.email,
          role: "viewer",
          addedDate: ""
        })));
      } else {
        setSharedWith([]);
      }
    } catch (err) {
      setVisibility("private");
      setSharedWith([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // AUTO-ADD: If user typed email but forgot to click "+"
      let finalSharedWith = [...sharedWith];
      const pendingEmail = newEmail.trim().toLowerCase();
      if (pendingEmail && pendingEmail.includes("@") && !finalSharedWith.find(s => s.email === pendingEmail)) {
        finalSharedWith.push({ email: pendingEmail, role: newRole, addedDate: new Date().toISOString() });
      }

      const itemId = getItemId();
      const currentUser = api.getCurrentUser();
      await api.setPermissions(itemId, {
        visibility,
        shared_with: finalSharedWith.map(s => s.email),
        shared_details: finalSharedWith,
        owner: currentUser?.email || file.owner || "",
        role: "editor",
        itemType: isFolder ? "folder" : "file",
        itemName: file.name
      });

      // Log activity
      const sharedNames = finalSharedWith.map(s => s.email).join(", ");
      const details = finalSharedWith.length > 0
        ? `Permissions for "${file.name}": ${visibility}, shared with ${sharedNames}`
        : `Permissions for "${file.name}": ${visibility}`;
      api.logActivity(itemId, "shared", details).catch(() => { });

      showToast("success", "Permissions updated successfully!");
      onUpdated && onUpdated();
      onClose();
    } catch (err) {
      showToast("error", "Failed to save: " + err.message);
    } finally {
      setSaving(false);
    }
  };

  const addEmail = () => {
    const email = newEmail.trim().toLowerCase();
    if (!email) return;
    if (!email.includes("@")) {
      showToast && showToast("error", "Please enter a valid email address");
      return;
    }
    if (sharedWith.find(s => s.email === email)) {
      showToast && showToast("info", "This email is already added");
      return;
    }
    setSharedWith([...sharedWith, { email, role: newRole, addedDate: new Date().toISOString() }]);
    setNewEmail("");
  };

  const removeEmail = (email) => {
    setSharedWith(sharedWith.filter(s => s.email !== email));
  };

  const updateRole = (email, role) => {
    setSharedWith(sharedWith.map(s => s.email === email ? { ...s, role } : s));
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    try {
      const d = new Date(dateStr);
      return d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
    } catch { return ""; }
  };

  if (!file) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-box manage-access-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <Icon name="shield" style={{ color: "#4285f4", fontSize: 20 }} />
          <h3>Manage Access</h3>
          <button className="modal-close-btn" onClick={onClose}>
            <Icon name="x" />
          </button>
        </div>

        {/* File/Folder Name */}
        <div className="manage-access-filename">
          <Icon name="file" style={{ color: isFolder ? "#f5ba13" : "#5f6368" }} />
          <span>{file.name}</span>
          <span className="manage-access-type-badge">
            {isFolder ? "Folder" : "File"}
          </span>
        </div>

        {loading ? (
          <div className="manage-access-loading">
            <Icon name="spinner" spin />
            <span>Loading permissions...</span>
          </div>
        ) : (
          <>
            {/* Visibility Selection */}
            <div className="manage-access-section">
              <label className="manage-access-label">Visibility</label>
              <div className="visibility-options">
                <button
                  className={`visibility-btn ${visibility === "private" ? "active" : ""}`}
                  onClick={() => setVisibility("private")}
                >
                  <Icon name="lock" />
                  <span>Private</span>
                  <small>Only you can access</small>
                </button>
                <button
                  className={`visibility-btn ${visibility === "shared" ? "active" : ""}`}
                  onClick={() => setVisibility("shared")}
                >
                  <Icon name="userGroup" />
                  <span>Shared</span>
                  <small>Specific people</small>
                </button>
                <button
                  className={`visibility-btn ${visibility === "public" ? "active" : ""}`}
                  onClick={() => setVisibility("public")}
                >
                  <Icon name="globe" />
                  <span>Public</span>
                  <small>All employees</small>
                </button>
              </div>
            </div>

            {/* Share With - shows for "shared" visibility */}
            {visibility === "shared" && (
              <div className="manage-access-section">
                <label className="manage-access-label">
                  Share with
                  {sharedWith.length > 0 && (
                    <span className="shared-count-badge">{sharedWith.length} user{sharedWith.length > 1 ? "s" : ""}</span>
                  )}
                </label>
                <div className="share-input-row">
                  <input
                    type="email"
                    value={newEmail}
                    onChange={(e) => setNewEmail(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && addEmail()}
                    placeholder="Enter email address..."
                    className="modal-input share-input"
                  />
                  <select
                    className="share-role-select"
                    value={newRole}
                    onChange={(e) => setNewRole(e.target.value)}
                  >
                    <option value="viewer">Viewer</option>
                    <option value="editor">Editor</option>
                    <option value="admin">Admin</option>
                  </select>
                  <button className="share-add-btn" onClick={addEmail} title="Add user">
                    <Icon name="plus" />
                  </button>
                </div>

                {/* Shared Users List */}
                {sharedWith.length > 0 && (
                  <div className="shared-users-list">
                    {sharedWith.map(user => (
                      <div key={user.email} className="shared-user-row">
                        <div className="shared-user-avatar">
                          {user.email.charAt(0).toUpperCase()}
                        </div>
                        <div className="shared-user-info">
                          <span className="shared-user-email">{user.email}</span>
                          {user.addedDate && (
                            <span className="shared-user-date">Added {formatDate(user.addedDate)}</span>
                          )}
                        </div>
                        <select
                          className="shared-user-role"
                          value={user.role}
                          onChange={(e) => updateRole(user.email, e.target.value)}
                        >
                          <option value="viewer">Viewer</option>
                          <option value="editor">Editor</option>
                          <option value="admin">Admin</option>
                        </select>
                        <button className="shared-user-remove" onClick={() => removeEmail(user.email)} title="Remove access">
                          <Icon name="x" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Hint text */}
                {sharedWith.length === 0 && !newEmail && (
                  <p className="share-hint-text">Type email + press Enter or click + to add users</p>
                )}
              </div>
            )}

            {/* Current Access Summary */}
            <div className="manage-access-section access-summary">
              <label className="manage-access-label">Access Summary</label>
              <div className="access-summary-content">
                {visibility === "private" && (
                  <div className="access-summary-item">
                    <Icon name="lock" style={{ color: "#ea4335" }} />
                    <span>Only you (owner) can access this {isFolder ? "folder" : "file"}</span>
                  </div>
                )}
                {visibility === "shared" && sharedWith.length === 0 && !newEmail && (
                  <div className="access-summary-item">
                    <Icon name="userGroup" style={{ color: "#ff9800" }} />
                    <span>Shared mode — add emails above to grant access</span>
                  </div>
                )}
                {visibility === "shared" && (sharedWith.length > 0 || newEmail) && (
                  <div className="access-summary-item">
                    <Icon name="userGroup" style={{ color: "#4285f4" }} />
                    <span>
                      Shared with <strong>{sharedWith.length + (newEmail.includes("@") ? 1 : 0)}</strong> user{(sharedWith.length + (newEmail.includes("@") ? 1 : 0)) > 1 ? "s" : ""}
                      {newEmail.includes("@") && <em> (including unsaved input)</em>}
                    </span>
                  </div>
                )}
                {visibility === "public" && (
                  <div className="access-summary-item">
                    <Icon name="globe" style={{ color: "#34a853" }} />
                    <span>All employees in the organization can access</span>
                  </div>
                )}
                {isFolder && visibility !== "private" && (
                  <div className="access-summary-item info">
                    <Icon name="info" style={{ color: "#4285f4" }} />
                    <span>Folder access applies to all files inside this folder</span>
                  </div>
                )}
              </div>
            </div>

            {/* Save Button */}
            <div className="modal-actions">
              <button className="modal-btn cancel" onClick={onClose}>Cancel</button>
              <button className="modal-btn create" onClick={handleSave} disabled={saving}>
                {saving ? (
                  <><Icon name="spinner" spin /> Saving...</>
                ) : (
                  <><Icon name="check" /> Save Permissions</>
                )}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default ManageAccessModal;
